import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - Add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('journi_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('journi_token');
      localStorage.removeItem('journi_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to get current user
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('journi_user');
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Creates a new ride posting.
 */
export const createRide = async (rideData) => {
  const user = getCurrentUser();
  if (!user) throw new Error('User not logged in');

  const response = await api.post('/rides/create', {
    driver_id: user.id,
    ...rideData
  });
  return response.data;
};

/**
 * Searches for available rides
 */
export const searchRides = async (searchParams) => {
  const response = await api.get('/rides/search', {
    params: searchParams
  });
  return response.data;
};

/**
 * Searches for rides passing near a location using polypoint matching
 */
export const searchRidesWithPolypoints = async (searchParams) => {
  const response = await api.get('/rides/search-polypoints', {
    params: searchParams
  });
  return response.data;
};

/**
 * Sends a request to join a specific ride
 */
export const requestRide = async (rideId, requestData = {}) => {
  const response = await api.post('/rides/request', { 
    ride_id: rideId,
    ...requestData
  });
  return response.data;
};

/**
 * Fetches the rides associated with the current user
 * Returns: { success, rides: { as_driver: [], as_rider: [] } }
 */
export const getMyRides = async () => {
  try {
    const response = await api.get('/dashboard/rides');
    
    if (!response.data || !response.data.rides) {
      return {
        success: false,
        rides: { as_driver: [], as_rider: [] }
      };
    }
    
    // Backend now properly returns separated arrays
    return {
      success: true,
      rides: {
        as_driver: response.data.rides.as_driver || [],
        as_rider: response.data.rides.as_rider || []
      }
    };
  } catch (error) {
    console.error('Failed to load rides:', error);
    return {
      success: false,
      rides: { as_driver: [], as_rider: [] }
    };
  }
};

/**
 * Get ride requests for driver's rides
 */
export const getDriverRequests = async () => {
  try {
    const response = await api.get('/dashboard/requests');
    return {
      success: true,
      requests: response.data.requests || []
    };
  } catch (error) {
    console.error('Failed to load driver requests:', error);
    return {
      success: false,
      requests: []
    };
  }
};


/**
 * Driver responds to ride request (accept/decline)
 */
export const respondToRideRequest = async (rideId, riderId, action, reason = null, requestId = null) => {
  const response = await api.post('/rides/respond', {
    ride_id: rideId,
    rider_id: riderId,
    request_id: requestId,
    action, // 'accept' or 'decline'
    reason
  });
  return response.data;
};

export const getCorridorMatchesForRide = async (rideId) => {
  const response = await api.get(`/rides/${rideId}/corridor-matches`);
  return response.data;
};

export const getRouteSwitchSuggestions = async (routeGroupId) => {
  const response = await api.get(`/rides/recurring/${routeGroupId}/route-switch-suggestions`);
  return response.data;
};

export const respondToRouteSwitchSuggestion = async (routeGroupId, suggestionId, action) => {
  const response = await api.post(`/rides/recurring/${routeGroupId}/switch-route`, {
    suggestion_id: suggestionId,
    action
  });
  return response.data;
};

export const sendNearMissOffer = async (offerData) => {
  const response = await api.post('/rides/near-miss-offer', offerData);
  return response.data;
};

export const submitRideRouteEdit = async (rideId, payload) => {
  const response = await api.post(`/rides/${rideId}/route-edit`, payload);
  return response.data;
};

export const submitRideRequestRouteEdit = async (requestId, payload) => {
  const response = await api.post(`/ride-requests/${requestId}/route-edit`, payload);
  return response.data;
};

export const respondToRouteEdit = async (editId, action) => {
  const response = await api.post(`/route-edits/${editId}/respond`, { action });
  return response.data;
};

/**
 * Cancel a ride (works for both driver and rider)
 */
export const cancelRide = async (rideId, role, reason = null) => {
  const response = await api.post('/rides/cancel', {
    ride_id: rideId,
    role, // 'driver' or 'rider'
    reason
  });
  return response.data;
};

/**
 * USER LOCATIONS API
 */

// Get user's saved locations
export const getUserLocations = async () => {
  const response = await api.get('/user/locations');
  return response.data;
};

// Add new location
export const addUserLocation = async (locationData) => {
  const response = await api.post('/user/locations', locationData);
  return response.data;
};

// Update location
export const updateUserLocation = async (locationId, updates) => {
  const response = await api.put('/user/locations', {
    id: locationId,
    ...updates
  });
  return response.data;
};

// Delete location
export const deleteUserLocation = async (locationId) => {
  const response = await api.delete('/user/locations', {
    data: { id: locationId }
  });
  return response.data;
};

// Get recommended rides near user's locations
export const getRecommendedRides = async () => {
  const response = await api.get('/user/rides/recommended');
  return response.data;
};

export const postRideRequest = async (requestData) => {
  const response = await api.post('/ride-requests/create', requestData);
  return response.data;
};

export const createReport = async (reportData) => {
  const response = await api.post('/reports', reportData);
  return response.data;
};

export const getMyReports = async () => {
  const response = await api.get('/reports');
  return response.data;
};

export const getPendingRatings = async () => {
  const response = await api.get('/ratings/pending');
  return response.data;
};

export const submitRating = async (ratingData) => {
  const response = await api.post('/ratings', ratingData);
  return response.data;
};

export const getRatingSummary = async (userId) => {
  const response = await api.get(`/ratings/summary/${userId}`);
  return response.data;
};

export const getRouteOptions = async (params) => {
  const response = await api.get('/rides/route-options', { params });
  return response.data;
};

/**
 * Fetches user's contact exchange status
 */
export const getUserExchangeStatus = async () => {
  const response = await api.get('/users/exchange-status');
  return response.data;
};

/**
 * Exchanges contact details for a booking
 */
export const exchangeContact = async (rideId, requestId) => {
  const response = await api.post('/rides/exchange-contact', {
    ride_id: rideId,
    request_id: requestId
  });
  return response.data;
};

/**
 * Applies a referral code to the user's account
 */
export const applyReferral = async (referralCode) => {
  const response = await api.post('/users/apply-referral', {
    referral_code: referralCode
  });
  return response.data;
};

// ======================================================
// 💺 SEAT & SEAT BIDDING API METHODS
// ======================================================

/**
 * Fetches all seats and occupant information for a ride
 */
export const getSeats = async (rideId) => {
  const response = await api.get(`/rides/${rideId}/seats`);
  return response.data;
};

/**
 * Selects an available seat
 */
export const selectSeat = async (rideId, seatCode, requestId) => {
  const response = await api.post(`/rides/${rideId}/seats/select`, {
    seat_code: seatCode,
    request_id: requestId
  });
  return response.data;
};

/**
 * Places a bid on an occupied seat
 */
export const placeBid = async (rideId, seatCode, bidAmount, requestId) => {
  const response = await api.post(`/rides/${rideId}/seats/bid`, {
    seat_code: seatCode,
    bid_amount: bidAmount,
    request_id: requestId
  });
  return response.data;
};

/**
 * Occupant accepts or declines a seat bid
 */
export const occupantRespond = async (bidId, responseText) => {
  const response = await api.post(`/rides/seats/bid/${bidId}/occupant-respond`, {
    response: responseText
  });
  return response.data;
};

/**
 * Driver approves or rejects a seat swap
 */
export const driverRespond = async (bidId, responseText) => {
  const response = await api.post(`/rides/seats/bid/${bidId}/driver-respond`, {
    response: responseText
  });
  return response.data;
};

/**
 * Get payment breakdown details for a seat bid
 */
export const getPaymentDetails = async (bidId) => {
  const response = await api.get(`/rides/seats/bid/${bidId}/payment-details`);
  return response.data;
};

/**
 * Confirm payment for a seat bid
 */
export const confirmPayment = async (bidId) => {
  const response = await api.post(`/rides/seats/bid/${bidId}/confirm-payment`);
  return response.data;
};

export const getBidWar = async (rideId) => {
  const response = await api.get(`/rides/${rideId}/bid-war`);
  return response.data;
};

export const placeBidWarBid = async (rideId, bidAmount) => {
  const response = await api.post(`/rides/${rideId}/bid-war/bid`, {
    bid_amount: bidAmount
  });
  return response.data;
};

export const initiateJazzCashBooking = async (rideRequestId, paymentMethod) => {
  const response = await api.post('/payments/jazzcash/initiate-booking', {
    ride_request_id: rideRequestId,
    payment_method: paymentMethod
  });
  return response.data;
};

export const initiateJazzCashRecurring = async (rideRequestIds, paymentMethod) => {
  const response = await api.post('/payments/jazzcash/initiate-recurring', {
    ride_request_ids: rideRequestIds,
    payment_method: paymentMethod
  });
  return response.data;
};

export const initiateJazzCashDeposit = async (paymentMethod) => {
  const response = await api.post('/payments/jazzcash/initiate-deposit', {
    payment_method: paymentMethod
  });
  return response.data;
};

export const initiateJazzCashSubscription = async (plan, paymentMethod) => {
  const response = await api.post('/payments/jazzcash/initiate-subscription', {
    plan,
    payment_method: paymentMethod
  });
  return response.data;
};

export const initiateJazzCashTopup = async (tokensCount, paymentMethod) => {
  const response = await api.post('/payments/jazzcash/initiate-topup', {
    tokens_count: tokensCount,
    payment_method: paymentMethod
  });
  return response.data;
};

/**
 * Fetches all notifications for the authenticated user
 */
export const getNotifications = async () => {
  const response = await api.get('/notifications');
  return response.data;
};

/**
 * Marks a notification as read
 */
export const markNotificationAsRead = async (notificationId) => {
  const response = await api.post(`/notifications/${notificationId}/read`);
  return response.data;
};

/**
 * Subscribe user to a plan
 */
export const subscribe = async (plan) => {
  const response = await api.post('/users/subscribe', { plan });
  return response.data;
};

export default api;
